/**
 * One memory budget shared between the two things that hold pixel bytes:
 * the parsed source file(s) held resident by instance-source, and the decoded
 * frames held by cornerstone's image cache.
 *
 * Why they have to share: for an uncompressed transfer syntax a decoded frame is
 * byte-for-byte a slice of the source file we're already holding, so a fixed
 * 512 MB frame cache sitting alongside a resident 430 MB cine means ~940 MB of
 * largely duplicated pixels. Sizing the frame cache as `budget − resident source`
 * keeps the total flat no matter how the study is shaped: many small files leave
 * nearly the whole budget for frames, one huge cine leaves the floor.
 *
 * Lives in its own module rather than in cornerstone-setup.js because
 * instance-source needs to report into it, and cornerstone-setup imports the
 * loaders which import instance-source. Only cornerstone-core is touched here,
 * which is a leaf.
 */

import cornerstone from "cornerstone-core";
import { recordCacheLimit, recordSourceResident } from "../session-stats";

/** Total for resident source files + decoded frames. */
const DEFAULT_BUDGET_BYTES = 512 * 1024 * 1024;

/**
 * The frame cache never shrinks below this, even when a single source file eats
 * the whole budget. A cine that big is exactly the case where re-decoding on
 * every scroll tick would be most painful, and the source file is resident
 * whether we cache frames or not.
 */
const MIN_CACHE_BYTES = 64 * 1024 * 1024;

let budgetBytes = DEFAULT_BUDGET_BYTES;
let sourceBytes = 0;
let largestFrameBytes = 0;
let appliedBytes = -1;

function applyCacheLimit(): void {
  // cornerstone's purgeCacheIfNecessary evicts LRU-last *including the image it
  // just added*, so if one frame is larger than the ceiling the cache empties
  // itself on every load and the viewer re-decodes forever. Keep headroom for a
  // few of the largest frame seen so far, whatever the budget says.
  const effective = Math.max(
    budgetBytes - sourceBytes,
    MIN_CACHE_BYTES,
    largestFrameBytes * 4
  );
  if (effective === appliedBytes) return;
  appliedBytes = effective;
  cornerstone.imageCache.setMaximumSizeBytes(effective);
  recordCacheLimit(effective);
}

/**
 * Set the total budget. Called with no argument to restore the default.
 *
 * Deliberately has no way to express "unbounded": lifting the ceiling on a
 * storage failure, with no path back, is what turned a recoverable hiccup into
 * an out-of-memory crash on a large study.
 */
export function setCacheBudget(bytes: number = DEFAULT_BUDGET_BYTES): void {
  budgetBytes = bytes;
  applyCacheLimit();
}

/**
 * Report the total bytes of source files currently held parsed in memory. The
 * frame cache gives up whatever this takes.
 */
export function noteSourceBytes(bytes: number): void {
  if (bytes === sourceBytes) return;
  sourceBytes = bytes;
  recordSourceResident(bytes);
  applyCacheLimit();
}

/**
 * Report the size of a decoded frame so the ceiling can stay above it. Cheap
 * enough to call on every decode; only ever raises the floor.
 */
export function noteFrameSize(bytes: number): void {
  if (!bytes || bytes <= largestFrameBytes) return;
  largestFrameBytes = bytes;
  applyCacheLimit();
}

export function getCacheBudget(): {
  budgetBytes: number;
  sourceBytes: number;
  largestFrameBytes: number;
  cacheLimitBytes: number;
} {
  return {
    budgetBytes,
    sourceBytes,
    largestFrameBytes,
    cacheLimitBytes: appliedBytes,
  };
}

applyCacheLimit();

// Console hook for attributing heap between the two pixel holders, e.g. when a
// heap reading looks larger than the budget should allow. Anything left over is
// headers, garbage not yet collected, or a genuine leak.
if (typeof window !== "undefined") {
  (window as any).pacsbinMemoryInfo = () => {
    const cache = cornerstone.imageCache.getCacheInfo();
    const info = {
      sourceResidentBytes: sourceBytes,
      cachedFrameBytes: cache.cacheSizeInBytes,
      cachedFrames: cache.numberOfImagesCached,
      cacheLimitBytes: cache.maximumSizeInBytes,
      budgetBytes,
      totalTrackedBytes: sourceBytes + cache.cacheSizeInBytes,
      heapUsedBytes: (performance as any).memory?.usedJSHeapSize ?? null,
    };
    console.table(info);
    return info;
  };
}
